import { makeStyles } from '@mui/styles';
import { alpha } from '@mui/material/styles';
import { BRAND_RED } from './constants';
import { STATUS_COLORS } from './constants';

export default makeStyles((theme) => {
	const isDark = theme.palette.mode === 'dark';

	return {
		dialogPaper: {
			borderRadius: theme.spacing(3),
			maxWidth: 640,
			maxHeight: '90vh',
			display: 'flex',
			flexDirection: 'column',
			overflow: 'hidden',
			border: '1px solid',
			borderColor: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 23, 42, 0.08)',
			boxShadow: isDark
				? '0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)'
				: '0 24px 64px rgba(15, 23, 42, 0.16), 0 0 0 1px rgba(15, 23, 42, 0.04)'
		},
		header: {
			position: 'relative',
			flexShrink: 0,
			zIndex: 2,
			padding: theme.spacing(2.25, 2),
			color: theme.palette.common.white,
			overflow: 'hidden',
			[theme.breakpoints.up('sm')]: {
				paddingLeft: theme.spacing(2.5),
				paddingRight: theme.spacing(2.5)
			},
			'&::before': {
				content: '""',
				position: 'absolute',
				inset: 0,
				background: 'radial-gradient(ellipse at 80% 20%, rgba(255,255,255,0.04) 0%, transparent 60%)',
				pointerEvents: 'none'
			},
			'&::after': {
				content: '""',
				position: 'absolute',
				width: 200,
				height: 200,
				right: -50,
				top: -80,
				borderRadius: '50%',
				pointerEvents: 'none'
			}
		},
		headerDefault: {
			background: `linear-gradient(135deg, #111111 0%, #1a1214 48%, ${alpha(BRAND_RED, 0.55)} 100%)`,
			'&::after': {
				background: `radial-gradient(circle, ${alpha(BRAND_RED, 0.3)} 0%, transparent 70%)`
			}
		},
		headerCompleted: {
			background: `linear-gradient(135deg, #0c1a14 0%, #0f2318 40%, ${alpha(STATUS_COLORS.completed, 0.45)} 100%)`,
			'&::after': {
				background: `radial-gradient(circle, ${alpha(STATUS_COLORS.completed, 0.3)} 0%, transparent 70%)`
			}
		},
		headerFailed: {
			background: `linear-gradient(135deg, #1a0c0c 0%, #231010 40%, ${alpha(STATUS_COLORS.failed, 0.4)} 100%)`,
			'&::after': {
				background: `radial-gradient(circle, ${alpha(STATUS_COLORS.failed, 0.3)} 0%, transparent 70%)`
			}
		},
		headerCancelling: {
			background: `linear-gradient(135deg, #170f29 0%, ${STATUS_COLORS.cancelling} 100%)`
		},
		headerCancelled: {
			background: `linear-gradient(135deg, #1a1508 0%, #23190a 40%, ${alpha(STATUS_COLORS.cancelled, 0.35)} 100%)`,
			'&::after': {
				background: `radial-gradient(circle, ${alpha(STATUS_COLORS.cancelled, 0.3)} 0%, transparent 70%)`
			}
		},
		stageChip: {
			'&&': {
				fontWeight: 700,
				height: 28,
				fontSize: '1.2rem',
				letterSpacing: '0.02em',
				backgroundColor: 'rgba(255,255,255,0.92)',
				color: '#1e293b',
				border: '1px solid rgba(255,255,255,0.95)'
			}
		},
		stageChipCompleted: {
			'&&': {
				backgroundColor: '#ecfdf5',
				color: '#047857',
				border: '1px solid rgba(16, 185, 129, 0.45)'
			}
		},
		stageChipFailed: {
			'&&': {
				backgroundColor: '#fef2f2',
				color: '#b91c1c',
				border: '1px solid rgba(239, 68, 68, 0.4)'
			}
		},
		stageChipCancelling: {
			'&&': {
				backgroundColor: '#f5f3ff',
				color: STATUS_COLORS.cancelling,
				border: `1px solid ${alpha(STATUS_COLORS.cancelling, 0.45)}`
			}
		},
		stageChipCancelled: {
			'&&': {
				backgroundColor: '#fffbeb',
				color: '#b45309',
				border: '1px solid rgba(245, 158, 11, 0.45)'
			}
		},
		stageChipProcessing: {
			'&&': {
				backgroundColor: '#fffbeb',
				color: '#b45309',
				border: '1px solid rgba(245, 158, 11, 0.45)'
			}
		},
		progressBar: {
			height: 6,
			borderRadius: 999,
			backgroundColor: 'rgba(0, 0, 0, 0.28)',
			'& .MuiLinearProgress-bar': {
				borderRadius: 999
			}
		},
		progressBarCompleted: {
			'& .MuiLinearProgress-bar': {
				background: `linear-gradient(90deg, #047857 0%, ${STATUS_COLORS.completed} 100%)`
			}
		},
		progressBarFailed: {
			'& .MuiLinearProgress-bar': {
				background: `linear-gradient(90deg, #b91c1c 0%, ${STATUS_COLORS.failed} 100%)`
			}
		},
		progressBarCancelling: {
			'& .MuiLinearProgress-bar': {
				backgroundColor: STATUS_COLORS.cancelling
			}
		},
		progressBarCancelled: {
			'& .MuiLinearProgress-bar': {
				background: `linear-gradient(90deg, #9c4706 0%, ${STATUS_COLORS.cancelled} 100%)`
			}
		},
		progressBarDefault: {
			'& .MuiLinearProgress-bar': {
				background: `linear-gradient(90deg, #c81e2c 0%, ${BRAND_RED} 100%)`
			}
		},
		progressBarIndeterminate: {
			height: 4,
			borderRadius: 999,
			backgroundColor: 'rgba(0, 0, 0, 0.28)',
			'& .MuiLinearProgress-bar': {
				borderRadius: 999
			}
		},
		progressBarIndeterminateDefault: {
			'& .MuiLinearProgress-bar': {
				backgroundColor: alpha(BRAND_RED, 0.8)
			}
		},
		progressBarIndeterminateCompleted: {
			'& .MuiLinearProgress-bar': {
				backgroundColor: alpha(STATUS_COLORS.completed, 0.8)
			}
		},
		progressBarIndeterminateFailed: {
			'& .MuiLinearProgress-bar': {
				backgroundColor: alpha(STATUS_COLORS.failed, 0.8)
			}
		},
		progressBarIndeterminateCancelling: {
			'& .MuiLinearProgress-bar': {
				backgroundColor: STATUS_COLORS.cancelling
			}
		},
		progressBarIndeterminateCancelled: {
			'& .MuiLinearProgress-bar': {
				backgroundColor: alpha(STATUS_COLORS.cancelled, 0.8)
			}
		},
		progressBarIndeterminateProcessing: {
			'& .MuiLinearProgress-bar': {
				backgroundColor: alpha(STATUS_COLORS.processing, 0.8)
			}
		}
	};
});
